import { useMutation, useQuery } from '@apollo/client';
import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  OAuthProvider,
  OAuthSetting,
  ReadOAuthProvidersArg,
  readOAuthProvidersQuery,
  ReadOAuthProvidersResponse,
  UpdateOAuthProviderArg,
  updateOAuthProviderMutation,
  UpdateOAuthProviderResponse,
} from 'apis/partnerIntegrations';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

import { OAuthProviderSettingsConfiguration } from '../../components/settingsConfiguration/oauthProviderSettingsConfiguration';
import { useOAuthAuthorization } from '../../hooks/useOAuthAuthorization';
import { useOAuthProviderConfigurationForm } from '../../hooks/useOAuthProviderConfigurationForm';
import styles from '../../oauthProviderConfigurationModal.module.scss';
import type {
  OAuthProviderConfigurationFormValues,
  OAuthProviderConfigurationModalResult,
} from '../../oauthProviderConfigurationModal.types';
import { OAuthProviderConfigurationModalLoading } from '../components/oauthProviderConfigurationModalLoading/oauthProviderConfigurationModalLoading';

type OAuthProviderUpdateModalProps = {
  isOpen: boolean;
  providerId: string;
  meta?: {
    resolve: (result: OAuthProviderConfigurationModalResult) => void;
  };
  hide: VoidFunction;
};

type ProviderFormValues = {
  provider: OAuthProvider;
};

/** A modal for updating OAuth providers */
export const OAuthProviderModalUpdate = ({
  isOpen,
  providerId,
  meta,
  hide,
}: OAuthProviderUpdateModalProps): JSX.Element => {
  const formId = uuid();
  const selectForm = useForm<ProviderFormValues>({
    defaultValues: { provider: undefined },
  });
  const selectedProvider = selectForm.watch('provider');

  const { connectionStatus, handleAuthorization, checkConnectionStatus } =
    useOAuthAuthorization(selectedProvider?.id);

  const notify = useNotifications();

  const { schema, values, isSchemaLoading, schemaError } =
    useOAuthProviderConfigurationForm({
      mode: 'update',
      strategyId: selectedProvider?.strategy,
      provider: selectedProvider,
    });

  const form = useForm<OAuthProviderConfigurationFormValues>({
    defaultValues: {},
    values,
  });

  // Fetch the OAuth provider if supplied
  const {
    loading: isProviderLoading,
    error: providerError,
    data,
  } = useQuery<ReadOAuthProvidersResponse, ReadOAuthProvidersArg>(
    readOAuthProvidersQuery,
    {
      skip: !providerId,
      variables: {
        id: providerId,
      },
    },
  );

  useEffect(() => {
    if (data) {
      const providerData = data.Management.oAuthProviders;
      if (providerData.length > 0) {
        selectForm.setValue('provider', providerData[0]);
      }
    }
  }, [data, selectForm]);

  const [updateProvider, { loading: isUpdatingProvider }] = useMutation<
    UpdateOAuthProviderResponse,
    UpdateOAuthProviderArg
  >(updateOAuthProviderMutation, {
    refetchQueries: [
      {
        query: readOAuthProvidersQuery,
        variables: { id: providerId },
      },
    ],
    awaitRefetchQueries: true,
    onError: (err) => {
      notify(
        {
          title: 'Error while updating the OAuth provider.',
          text: err.cause?.toString() ?? err?.toString(),
        },
        NotificationType.Error,
      );
    },
    onCompleted: (result) => {
      const provider = result.Management.updateOAuthProvider;

      notify(
        {
          title: 'Success',
          text: `"${provider.name}" OAuth provider has been updated.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      resolveModalResponse(provider);
    },
  });

  const isLoading = isUpdatingProvider || isProviderLoading || isSchemaLoading;

  const onSubmit = (formData: OAuthProviderConfigurationFormValues) => {
    const settings: OAuthSetting[] = Object.entries(formData)
      .filter(([key, setting]) => key !== 'name' && setting.isEditing)
      .map(([key, setting]) => ({
        key,
        value: setting.value,
        secret: setting.secret,
      }));

    updateProvider({
      variables: {
        id: selectedProvider?.id,
        name: formData.name.value as string,
        settings,
      },
    });
  };

  const resolveModalResponse = useCallback(
    (provider: OAuthProvider) => {
      meta?.resolve({
        isSuccessful: true,
        provider,
      });
    },
    [meta],
  );

  const renderContent = () => {
    if (isLoading) {
      return <OAuthProviderConfigurationModalLoading />;
    }

    if (providerError) {
      return (
        <FormField>
          <Text.Paragraph>
            Error loading OAuth provider. Please try again or contact support.
          </Text.Paragraph>
        </FormField>
      );
    }

    if (schemaError) {
      return (
        <FormField>
          <Text.Paragraph>
            Failed to load OAuth provider configuration schema. Please try again
            later or contact support.
          </Text.Paragraph>
        </FormField>
      );
    }

    if (!selectedProvider?.strategy) {
      return (
        <FormField>
          <Text.Paragraph>
            Something went wrong, there is no provider to update. Please contact
            support.
          </Text.Paragraph>
        </FormField>
      );
    }

    return <OAuthProviderSettingsConfiguration mode="update" schema={schema} />;
  };

  // Determine the footer buttons based on the current step
  const renderModalFooter = () => {
    return (
      <ModalFoot
        confirmOnClick={async () => {
          await handleAuthorization();
          await checkConnectionStatus();

          if (connectionStatus === 'CONNECTED') {
            notify(
              {
                title: 'Success',
                text: `"${selectedProvider.name}" OAuth provider has been authorized.`,
                icon: 'i-done',
                time: 5000,
              },
              NotificationType.Success,
            );
          }
        }}
        confirmSecondaryOnClick={form.handleSubmit(onSubmit)}
        cancelOnClick={hide}
        confirmName={
          connectionStatus === 'CONNECTED' ? 'Reauthorize' : 'Authorize'
        }
        confirmSecondaryName="Update Provider"
        cancelName="Cancel"
        confirmProps={{
          variant: 'primary',
          isLoading,
        }}
        confirmSecondaryProps={{
          type: 'submit',
          form: formId,
          isLoading,
          disabled: isLoading || !selectedProvider?.id,
        }}
        cancelProps={{
          variant: 'secondary',
        }}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={hide}
      className={styles.oauthProviderConfigurationModal}
    >
      <ModalHead
        title={<Text.Heading>Update OAuth provider</Text.Heading>}
        onClose={hide}
      />

      <ModalBody>
        <FormProvider {...form}>
          <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
            <FormGrid>{renderContent()}</FormGrid>
          </form>
        </FormProvider>
      </ModalBody>

      {renderModalFooter()}
    </Modal>
  );
};
